import { type RefObject, useCallback, useEffect, useRef } from 'react'

type UseMarketplacePartnerRegisterPageInfiniteScrollHandlerParams = {
  hasMore: boolean
  isLoading: boolean
  onLoadMore: () => void
}

type UseMarketplacePartnerRegisterPageInfiniteScrollHandlerReturn = {
  handleScroll: () => void
  scrollContainerRef: RefObject<HTMLDivElement | null>
}
/* 2000 pixels from the bottom to trigger load more form smooth scrolling */
const SCROLL_THRESHOLD = 2000

export const useMarketplacePartnerRegisterPageInfiniteScrollHandler = ({
  hasMore,
  isLoading,
  onLoadMore,
}: UseMarketplacePartnerRegisterPageInfiniteScrollHandlerParams): UseMarketplacePartnerRegisterPageInfiniteScrollHandlerReturn => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container || !hasMore || isLoading) {
      return
    }

    const { clientHeight, scrollHeight, scrollTop } = container

    if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD) {
      onLoadMore()
    }
  }, [hasMore, isLoading, onLoadMore])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) {
      return
    }

    container.addEventListener('scroll', handleScroll)

    return (): void => {
      container.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  return {
    handleScroll,
    scrollContainerRef,
  }
}
